import React, { useCallback, useEffect, useState } from 'react';
import { Award, Loader, Plus, Info, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  opportunityBenefitsAPI,
  OpportunityBenefit,
  BenefitType,
  BENEFIT_TYPE_META,
  formatBenefitValue
} from '../../services/opportunityBenefitsAPI';

interface OpportunityBenefitsSectionProps {
  projectId: number;
  opportunityId: number;
}

/**
 * Benefits panel inside an opportunity card's Overview tab.
 * Shows first-class benefit records (savings / growth / efficiency / controls)
 * with projected vs realized values, source citation and formula version.
 * Growth benefits can be added manually (no automated data source exists yet).
 */
const OpportunityBenefitsSection: React.FC<OpportunityBenefitsSectionProps> = ({ projectId, opportunityId }) => {
  const [benefits, setBenefits] = useState<OpportunityBenefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [realizedInput, setRealizedInput] = useState('');
  const [savingRealized, setSavingRealized] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    benefit_type: 'growth_capacity' as BenefitType,
    metric_label: '',
    projected_value: '',
    unit: 'per_week',
    citation: ''
  });
  const [savingAdd, setSavingAdd] = useState(false);

  const fetchBenefits = useCallback(async () => {
    try {
      setLoading(true);
      const data = await opportunityBenefitsAPI.getBenefits(projectId, { opportunity_id: opportunityId });
      setBenefits(data);
    } catch {
      // Benefits are supplementary — fail quietly in the card, keep the rest usable
      setBenefits([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, opportunityId]);

  useEffect(() => { fetchBenefits(); }, [fetchBenefits]);

  const startRealizedEdit = (benefit: OpportunityBenefit) => {
    setEditingId(benefit.id);
    setRealizedInput(benefit.realized_value !== null ? String(parseFloat(benefit.realized_value)) : '');
  };

  const saveRealized = async (benefit: OpportunityBenefit) => {
    const trimmed = realizedInput.trim();
    const value = trimmed === '' ? null : parseFloat(trimmed);
    if (trimmed !== '' && isNaN(value as number)) {
      toast.error('Realized value must be a number');
      return;
    }
    try {
      setSavingRealized(true);
      const updated = await opportunityBenefitsAPI.recordRealized(projectId, benefit.id, { realized_value: value });
      setBenefits(prev => prev.map(b => (b.id === updated.id ? { ...b, ...updated } : b)));
      setEditingId(null);
      toast.success(value === null ? 'Realized value cleared' : 'Realized value recorded');
    } catch {
      toast.error('Failed to save realized value');
    } finally {
      setSavingRealized(false);
    }
  };

  const saveManualBenefit = async () => {
    if (!addForm.metric_label.trim()) {
      toast.error('Please enter a metric name');
      return;
    }
    const projected = parseFloat(addForm.projected_value);
    if (isNaN(projected)) {
      toast.error('Projected value must be a number');
      return;
    }
    const metricKey = addForm.metric_label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 100);
    if (!metricKey) {
      toast.error('Metric name must contain letters or numbers');
      return;
    }
    try {
      setSavingAdd(true);
      await opportunityBenefitsAPI.createBenefit(projectId, {
        opportunity_id: opportunityId,
        benefit_type: addForm.benefit_type,
        metric_key: metricKey,
        metric_label: addForm.metric_label.trim(),
        projected_value: projected,
        unit: addForm.unit,
        citation: addForm.citation.trim() || undefined
      });
      toast.success('Benefit added');
      setShowAddForm(false);
      setAddForm({ benefit_type: 'growth_capacity', metric_label: '', projected_value: '', unit: 'per_week', citation: '' });
      fetchBenefits();
    } catch {
      toast.error('Failed to add benefit');
    } finally {
      setSavingAdd(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="opportunity-benefits-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
          <Award className="w-3.5 h-3.5 text-indigo-500" /> Benefits — Projected vs Realized
        </h4>
        <button
          onClick={(e) => { e.stopPropagation(); setShowAddForm(v => !v); }}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
          data-testid="benefit-add-toggle"
        >
          <Plus className="w-3 h-3" /> Add Benefit
        </button>
      </div>

      {showAddForm && (
        <div className="mb-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg space-y-2" data-testid="benefit-add-form" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={addForm.benefit_type}
              onChange={(e) => setAddForm(f => ({ ...f, benefit_type: e.target.value as BenefitType }))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white"
              data-testid="benefit-add-type"
            >
              {(Object.keys(BENEFIT_TYPE_META) as BenefitType[]).map(t => (
                <option key={t} value={t}>{BENEFIT_TYPE_META[t].label}</option>
              ))}
            </select>
            <select
              value={addForm.unit}
              onChange={(e) => setAddForm(f => ({ ...f, unit: e.target.value }))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white"
            >
              <option value="per_week">per week</option>
              <option value="count">count</option>
              <option value="pct">%</option>
              <option value="usd_per_year">$ / year</option>
              <option value="hours_per_year">hours / year</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Metric name (e.g. Additional volume handled)"
            value={addForm.metric_label}
            onChange={(e) => setAddForm(f => ({ ...f, metric_label: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5"
            data-testid="benefit-add-label"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder="Projected value"
              value={addForm.projected_value}
              onChange={(e) => setAddForm(f => ({ ...f, projected_value: e.target.value }))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1.5"
              data-testid="benefit-add-projected"
            />
            <input
              type="text"
              placeholder="Source / citation (optional)"
              value={addForm.citation}
              onChange={(e) => setAddForm(f => ({ ...f, citation: e.target.value }))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={saveManualBenefit}
              disabled={savingAdd}
              className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
              data-testid="benefit-add-save"
            >
              {savingAdd ? 'Saving…' : 'Save Benefit'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
          <Loader className="w-3.5 h-3.5 animate-spin" /> Loading benefits…
        </div>
      ) : benefits.length === 0 ? (
        <p className="text-xs text-gray-400 py-1">
          No benefit records yet — they are created automatically when metrics are imported, or add one manually.
        </p>
      ) : (
        <div className="space-y-1.5">
          {benefits.map((benefit) => {
            const meta = BENEFIT_TYPE_META[benefit.benefit_type];
            const isEditing = editingId === benefit.id;
            return (
              <div
                key={benefit.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-gray-100 hover:border-gray-200 transition-colors"
                data-testid="benefit-record"
              >
                <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.bg} ${meta.color} border ${meta.border}`}>
                  {meta.label}
                </span>
                <span className="flex-1 min-w-0 text-xs text-gray-700 truncate" title={benefit.metric_label || benefit.metric_key}>
                  {benefit.metric_label || benefit.metric_key}
                </span>
                {(benefit.citation || benefit.formula_version) && (
                  <span
                    className="shrink-0 text-gray-300 hover:text-gray-500 cursor-help"
                    title={`${benefit.citation || ''}${benefit.formula_version ? ` (formula v${benefit.formula_version})` : ''} — source: ${benefit.source}`}
                    data-testid="benefit-citation"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </span>
                )}
                <span className="shrink-0 text-xs font-semibold text-gray-800" data-testid="benefit-projected">
                  {formatBenefitValue(benefit.projected_value, benefit.unit)}
                </span>
                <span className="shrink-0 text-[10px] text-gray-400">projected</span>
                {isEditing ? (
                  <span className="shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      value={realizedInput}
                      onChange={(e) => setRealizedInput(e.target.value)}
                      className="w-20 text-xs border border-indigo-300 rounded px-1.5 py-0.5"
                      autoFocus
                      data-testid="benefit-realized-input"
                    />
                    <button onClick={() => saveRealized(benefit)} disabled={savingRealized} className="text-green-600 hover:text-green-700 disabled:opacity-50" data-testid="benefit-realized-save">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); startRealizedEdit(benefit); }}
                    className={`shrink-0 text-xs px-1.5 py-0.5 rounded transition-colors ${
                      benefit.realized_value !== null
                        ? 'font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                        : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                    }`}
                    title={benefit.realized_value !== null ? `Realized as of ${benefit.realized_as_of_date || '—'} — click to edit` : 'Record realized value'}
                    data-testid="benefit-realized"
                  >
                    {benefit.realized_value !== null
                      ? `${formatBenefitValue(benefit.realized_value, benefit.unit)} realized`
                      : '+ realized'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OpportunityBenefitsSection;
